import * as React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { AiSearchResultItem } from '../../services/aiSearchService';
import { aiFonteFromItem } from '../../utils/aiFonteDisplay';

type PayloadField = { label: string; value: string };

function payloadText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s || null;
}

function payloadTextOrDash(value: unknown): string {
  return payloadText(value) ?? '—';
}

function buildPayloadFields(item: AiSearchResultItem): PayloadField[] {
  const p = item.payload;

  const always = (label: string, key: string): PayloadField => ({
    label,
    value: payloadTextOrDash(p[key]),
  });

  const optional = (label: string, key: string): PayloadField | null => {
    const v = payloadText(p[key]);
    return v ? { label, value: v } : null;
  };

  switch (item.entityType) {
    case 'intervention': {
      const expectedSaving =
        payloadText(p.expected_economic_saving) ?? payloadText(p.expected_economical_saving);
      return [
        always('Titolo', 'title'),
        always('Descrizione', 'description'),
        always('Tipo', 'type'),
        always('Commenti', 'comments'),
        { label: 'Investimento', value: payloadTextOrDash(p.investment) },
        { label: 'Risparmio economico atteso', value: expectedSaving ?? '—' },
        { label: 'Payback', value: payloadTextOrDash(p.payback_time) },
      ];
    }
    case 'energy_use':
      return [always('Codice', 'code'), always('Descrizione', 'description')];
    case 'indicator':
      return [
        always('Codice', 'code'),
        always('Descrizione', 'description'),
        optional('Formula', 'formula'),
      ].filter((f): f is PayloadField => f !== null);
    default:
      return [];
  }
}

export const AiSearchPayloadSummary: React.FC<{ item: AiSearchResultItem }> = ({ item }) => {
  const fields = buildPayloadFields(item);
  const fonte = aiFonteFromItem(item);
  if (!fields.length && !fonte) return null;

  return (
    <Stack spacing={1} sx={{ mt: 0.5, pr: 1 }}>
      {fonte ? (
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}
        >
          <Box component="span" sx={{ color: 'text.primary', letterSpacing: 0.2 }}>
            Fonte:
          </Box>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {fonte}
          </Box>
        </Typography>
      ) : null}
      {fields.map(({ label, value }) => (
        <Typography
          key={label}
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.45,
          }}
        >
          <Box
            component="span"
            sx={{ color: 'text.primary', letterSpacing: 0.2 }}
          >
            {label}:
          </Box>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {value}
          </Box>
        </Typography>
      ))}
    </Stack>
  );
};
